// src/graphql/pimcore/common/hooks/useQueryFallbackResponse.ts

import { useState, useEffect } from 'react';
import type { DocumentNode } from 'graphql';
import type { LocaleCode } from '../../../../utils/Locale';
import { fetchFallbackQueryResponse } from '../tools/fallbackQueryTools';
import { SiteContentPath } from '../../modules/siteContent/types/siteContentPaths';

type useQueryFallbackResponseParams<
    Q extends DocumentNode & { fallbackKey: K },
    K extends string,
> = {
    /** The GraphQL query to be executed. Must have a `fallbackKey` property. */
    query: Q;
    /** The locale code for the query, if applicable. */
    locale?: LocaleCode;
    /** Optional sub-path, currently only applicable for GET_SITE_CONTENT_ELEMENT_LISTING_QUERY */
    subPath?: SiteContentPath;
    /** Whether the real network was already invoked. Fallback data is not loaded unless this is true. */
    initialized: boolean;
    /** Query error, if any. If provided, fallback data will be loaded. */
    queryError: any;
};

/**
 * Hook to load fallback data for a given GraphQL query if the main query fails.
 *
 * This is useful for scenarios where you want to provide a graceful fallback. When the main query
 * fails, it logs an error and attempts to load the fallback data from a JSON file.
 * Informs weback to lazy-load the fallback data files, so they are loaded on demand and not
 * included in the main bundle.
 *
 * Fallback JSON files can be created/updated with
 * ```shell
 * REACT_APP_PIMCORE_GRAPHQL_API_KEY=*** npm run update-pimcore-graphql-fallback-responses
 * ```
 */
export function useQueryFallbackResponse<
    Q extends DocumentNode & { fallbackKey: K },
    K extends string,
>({
    query,
    locale,
    subPath,
    initialized,
    queryError,
}: useQueryFallbackResponseParams<Q, K>): {
    /** The fallback data imported from a hard-coded JSON file. */
    fallbackData: any;
    loading: boolean;
    error: any;
} {
    const [fallbackData, setFallbackData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);

    const resetFallback = () => {
        setFallbackData(null);
        setError(null);
        setLoading(false);
    };

    useEffect(() => {
        if (!initialized || !queryError) return;

        const { fallbackKey } = query as any;

        console.error(
            `Network query failed for ${fallbackKey}, falling back to hard-coded data`,
            queryError
        );

        setLoading(true);
        fetchFallbackQueryResponse(fallbackKey, locale, subPath)
            .then((data) => {
                setFallbackData(data);
                setError(null);
            })
            .catch((err) => {
                console.error('Failed to load fallback response:', err);
                setError(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [query, locale, subPath, initialized, queryError]);

    useEffect(() => {
        // whenever the locale (or query document, or subPath) changes,
        // we no longer want to hold onto an old fallbackData or error:
        resetFallback();
    }, [locale, subPath, query.fallbackKey]);

    return { fallbackData, loading, error };
}
